import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { differenceInDays, parseISO } from "date-fns";

import { useLocalization } from "@fluent/react";

import { formatNumber, Tag } from "@narmi/design_system";
import { entities } from "byzantine";
import ApiHttp from "byzantine/src/ApiHttp";
import { createInternationalWireFromAPISwiftTransaction, type InternationalWire } from "byzantine/src/dbbl/businessLogic/modules/internationalWires/providers";
import { getDate, getDisplayState } from "byzantine/src/dbbl/businessLogic/modules/internationalWires/utils";
import Filters from "byzantine/src/filters";
import Wire from "byzantine/src/Wire";
import { useNotificationContext } from "cerulean";

import Account from "byzantine/src/Account";
import SectionCard from "../SectionCard";
import UserFeaturesContext from "../contexts/UserFeaturesContext";
import AccountContext from "../contexts/AccountContext";
import WireStatusesDialog from "./WireStatusesDialog";

interface WireDescriptionProps {
  wire: Wire | InternationalWire;
  recipients: API.Recipient[];
  internationalWiresEnabled: boolean;
}

const WireDescription = ({ wire, recipients, internationalWiresEnabled }: WireDescriptionProps) => {
  const { l10n } = useLocalization();
  const hasTemplate = (wire as InternationalWire)?.templateName || (wire as Wire).template_name;

  const recipient = useMemo(() => (
      recipients.find(r => {
        if ((wire as Wire).to_account_number && (wire as Wire).to_account_routing_number) {
          return (
            r.fedwire_destination?.account_number === (wire as Wire).to_account_number &&
            r.fedwire_destination?.routing_number === (wire as Wire).to_account_routing_number
          )
        }
        if ((wire as InternationalWire).swiftDestinationId) {
          return r.id === (wire as InternationalWire).recipientId;
        }
        return false;
      }) || null
    ), [recipients, wire]);

  const beneficiaryName =
    recipient?.nickname ||
    recipient?.name ||
    (wire as Wire).beneficiary_name;
  const wireType = (wire as InternationalWire)?.swiftDestinationId
    ? "International"
    : "Domestic";
  const title = internationalWiresEnabled ? l10n.getString("wire-type-transactions-title", { wireType, beneficiaryName })
    : l10n.getString("transactions-title", { beneficiaryName });

  return (
    <div className="alignChild--left--top">
      {title}
      {hasTemplate ? (
        <div className="padding--x--xs">
          <Tag label={l10n.getString("wire-type-template")} kind="outline" />
        </div>
      ) : null}
    </div>
  );
};

interface PendingWireTableProps {
  wires: (Wire | InternationalWire)[];
  infoIcon: ReactNode;
  recipients: API.Recipient[];
  internationalWiresEnabled: boolean;
}

function deltaDays(obj: Wire | InternationalWire): number {
  const now: Date = new Date();
  const wireDate = getDate(obj);
  return differenceInDays(now, parseISO(wireDate));
}

const PendingWireTable = ({
  wires,
  infoIcon,
  recipients,
  internationalWiresEnabled,
}: PendingWireTableProps) => {
  const { l10n } = useLocalization();
  return (
    <div className="card-table three sparse">
      <div className="row-wrapper">
        <div className="row-item-header">
          {l10n.getString("transactions-table-header-description")}
        </div>
        <div className="row-item-header">
          {l10n.getString("transactions-table-header-status")}
          {infoIcon}
        </div>
        <div className="row-item-header align-right">
          {l10n.getString("transactions-table-header-amount")}
        </div>
      </div>
      {wires.map((wire) => (
        <div key={wire.id} className="row-wrapper">
          <div className="row-items-column">
            <div className="row-item">
              <WireDescription wire={wire} recipients={recipients} internationalWiresEnabled={internationalWiresEnabled}  />
              <div className="fontSize--s fontColor--secondary">
                {Filters.longMonthDayYear(getDate(wire))}
              </div>
            </div>
          </div>
          <div className="row-items-column reverse">
            <div className="row-item responsive-text-styling">
              {/* <linear.app/narmi/issue/NDS-1735> Will handle this translation */}
              {getDisplayState(wire)}
            </div>
            <div className="row-item align-right number">
              {formatNumber(wire.amount)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

interface PendingWiresCardProps {
  accountUuid: string;
}

interface UserFeatures {
  international_wires: boolean;
  international_wires_consumer: boolean;
  payment_activity: boolean;
}

const PendingWiresCard = ({ accountUuid }: PendingWiresCardProps) => {
  const { l10n } = useLocalization();
  const features = useContext(UserFeaturesContext) as UserFeatures;
  const { sendNotification } = useNotificationContext();
  const { accounts } = useContext(AccountContext) as { accounts: Account[] };
  const recipients = entities.recipients.useRecipients();
  const [isCardVisible, setIsCardVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingWires, setPendingWires] = useState<Wire[]>([]);
  const [internationalWires, setInternationalWires] = useState<InternationalWire[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const internationalWiresEnabled =
    features.international_wires || features.international_wires_consumer;

  const isValidWireSource = !!accounts
    .filter((a) => a.isInternal() && a.isValidTransferSource())
    .find((a) => a.id === accountUuid);

  if (!isValidWireSource || features.payment_activity) return null;

  const infoIcon = (className: string) => (
    <span
      className={`${className} narmi-icon-info clickable`}
      onClick={() => setIsDialogOpen(true)}
      onKeyUp={({ key }) => {
        if (key === "Enter") setIsDialogOpen(true);
      }}
      aria-label={l10n.getString("label-open-status-dialog")}
      role="button"
      tabIndex={0}
    />
  );

  useEffect(() => {
    const getPendingWires = async () => {
      const fetchedPendingWires = await Wire.getPendingWires(accountUuid);
      setPendingWires(fetchedPendingWires);
    };

    const fetchInternationalWires = async () => {
      const swiftTransactions = internationalWiresEnabled
        ? (
            await ApiHttp.fetch(
              "swift_transactions",
              { method: "GET" }
            )
          ).results
        : [];

      const mappedInternationalWires = swiftTransactions.map(createInternationalWireFromAPISwiftTransaction);
      setInternationalWires(mappedInternationalWires);
    };
    try {
      setIsLoading(true);
      getPendingWires();
      fetchInternationalWires();
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("error-fetching-pending-wires"),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sortedFilteredWires = useMemo(() => [...pendingWires, ...internationalWires]
      .filter((wire) => deltaDays(wire) < 30)
      .sort(
      (a, b) => {
        const dateA = getDate(a);
        const dateB = getDate(b);
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }
    ), [pendingWires, internationalWires]);

  return (
    <SectionCard
      isLoading={isLoading}
      isExpanded={isCardVisible}
      setIsExpanded={setIsCardVisible}
      kind={isCardVisible ? "transactions" : undefined}
      title={l10n.getString("recent-transactions-header-wires")}
      titleIcon={infoIcon("pending-wires-info-icon")}
    >
      {sortedFilteredWires?.length > 0 && (
        <>
          <PendingWireTable
            wires={sortedFilteredWires}
            recipients={recipients}
            internationalWiresEnabled={internationalWiresEnabled}
            infoIcon={infoIcon("margin--left--xxs")}
          />
          <WireStatusesDialog
            isDialogOpen={isDialogOpen}
            closeDialog={() => setIsDialogOpen(false)}
          />
        </>
      )}
    </SectionCard>
  );
};
export default PendingWiresCard;
